import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import AppIcon from '../assets/image/icon.png';


function Login() {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [error, setError] = useState("");
  const [loading, setIsLoading] = useState(false);


  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "GET_LOCATION_INFO"
        })
      );
    } else {
      toast.error("Location service not available");
    }
  }, [])

  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobile(val);
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mobile.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    setIsLoading(true);

    try {
      const result = await apiCall("login", { number: mobile }, false);
      if (result.status === true) {
        navigate("/verify-otp", { state: { mobile, token: result.data.otp_token, prevPage: 'login' } });
      } else if (result.response_code === 3) {
        navigate(`/register`, { state: { mobile: mobile } });
      } else {
        toast.error(result.message || "An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col mx-auto"
      style={{
        background: APP_THEME.bgColor,
        maxWidth: "480px",
        padding: "64px 24px 24px",
      }}
    >
      {/* Logo */}
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3"
        style={{
          background: `linear-gradient(135deg, ${APP_THEME.primaryLight}, ${APP_THEME.bgColor})`,
          border: `1px solid ${APP_THEME.primaryColor}20`,
        }}
      >
        <img
          src={AppIcon}
          alt="App Icon"
          className="w-18 h-18 rounded-xl"
          style={{
            objectFit: 'contain'
          }}
        />
      </div>

      <div
        className="text-base font-bold mb-8"
        style={{
          color: APP_THEME.primaryColor,
          fontFamily: APP_THEME.fontFamily,
        }}
      >
      </div>

      <h1
        className="text-2xl font-bold mb-2 text-left"
        style={{
          color: APP_THEME.textPrimary,
          fontFamily: APP_THEME.fontFamily,
        }}
      >
        Login In
      </h1>

      <p
        className="text-sm mb-8 text-left"
        style={{
          color: APP_THEME.textSecondary,
          fontFamily: APP_THEME.fontFamily,
        }}
      >
        Enter your mobile number to continue
      </p>

      <form onSubmit={handleSubmit} className="w-full max-w-[360px]">
        <div
          className="flex items-center overflow-hidden transition-colors duration-200"
          style={{
            border: `1px solid ${error ? APP_THEME.errorColor : APP_THEME.borderColor}`,
            borderRadius: APP_THEME.borderRadiusInput,
            background: APP_THEME.bgColor,
          }}
        >
          <div
            className="px-3.5 py-3 text-sm font-semibold whitespace-nowrap"
            style={{
              background: APP_THEME.surfaceColor,
              color: APP_THEME.textPrimary,
              fontFamily: APP_THEME.fontFamily,
              borderRight: `1px solid ${APP_THEME.borderColor}`,
            }}
          >
            +91
          </div>
          <input
            type="tel"
            maxLength={10}
            pattern="[0-9]*"
            placeholder="10-digit mobile number"
            value={mobile}
            onChange={handleChange}
            disabled={loading}
            className="flex-1 px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60"
            style={{
              fontFamily: APP_THEME.fontFamily,
              color: APP_THEME.textPrimary,
            }}
          />
        </div>
        {error && (
          <div
            className="text-xs mt-1.5"
            style={{
              color: APP_THEME.errorColor,
              fontFamily: APP_THEME.fontFamily,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 border-none rounded-lg text-[15px] font-semibold mt-5 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            background: loading ? APP_THEME.primaryDark : APP_THEME.primaryColor,
            color: APP_THEME.textOnPrimary,
            fontFamily: APP_THEME.fontFamily,
          }}
          onMouseOver={(e) => {
            if (!loading) e.currentTarget.style.background = APP_THEME.primaryDark;
          }}
          onMouseOut={(e) => {
            if (!loading) e.currentTarget.style.background = APP_THEME.primaryColor;
          }}
          onMouseDown={(e) => {
            if (!loading) e.currentTarget.style.transform = "scale(0.97)";
          }}
          onMouseUp={(e) => {
            if (!loading) e.currentTarget.style.transform = "scale(1)";
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2">
              <div
                className="w-5 h-5 rounded-full animate-spin"
                style={{
                  border: `2px solid ${APP_THEME.textOnPrimary}`,
                  borderTop: `2px solid transparent`,
                }}
              />
              Sending...
            </div>
          ) : (
            "Send OTP"
          )}
        </button>
      </form>

      {/* Footer section - now properly positioned at bottom */}
      <div className="flex-1" />

      <div className="w-full pt-10 flex flex-col items-center">
        <div
          className="text-sm text-center mb-3"
          style={{
            color: APP_THEME.textSecondary,
            fontFamily: APP_THEME.fontFamily,
          }}
        >
          New here?{" "}
          <span
            onClick={() => !loading && navigate("/register")}
            className="font-semibold"
            style={{
              color: APP_THEME.primaryColor,
              cursor: !loading ? "pointer" : "not-allowed",
              opacity: loading ? 0.5 : 1,
            }}
          >
            Register
          </span>
        </div>

        <div
          className="text-xs text-center leading-relaxed"
          style={{
            color: APP_THEME.textSecondary,
            fontFamily: APP_THEME.fontFamily,
          }}
        >
          By continuing you agree to our{" "}
          <span
            onClick={() =>
              window.open(
                APP_THEME.policyUrls?.terms,
                "_blank",
                "noopener,noreferrer"
              )
            }
            className="inline-block"
            style={{
              color: APP_THEME.primaryColor,
              cursor: !loading ? "pointer" : "not-allowed",
              opacity: loading ? 0.5 : 1,
            }}
          >
            Terms
          </span>{" "}
          &{" "}
          <span
           onClick={() =>
              window.open(
                APP_THEME.policyUrls?.privacy,
                "_blank",
                "noopener,noreferrer"
              )
            }
            className="inline-block"
            style={{
              color: APP_THEME.primaryColor,
              cursor: !loading ? "pointer" : "not-allowed",
              opacity: loading ? 0.5 : 1,
            }}
          >
            Privacy
          </span>
        </div>
      </div>
      <Toaster position="bottom-center" />
    </div>
  );
}

export default Login;